import { FloatingPortal } from '@floating-ui/react';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react-dom';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { CSSProperties } from 'styled-components';

import { isDefined } from '../../../utils/variables';
import { useDialog } from '../../hooks/use-dialog';
import { useElementWidth } from '../../hooks/use-element-width';
import { useKeyBinding } from '../../hooks/use-key-binding';
import { useKeyHandler } from '../../hooks/use-key-handler';
import { ConfirmationDialog } from '../confirmation-dialog/confirmation-dialog';
import { Icon } from '../icon/icon';
import { Stack } from '../stack/stack';
import { TextBox } from '../text-box/text-box';

type BaseOption = {
  id: string;
  name: string; // used as default search text and chip text
};

type RenderOption<T> = (option: T) => React.ReactNode;
type FilterPredicate<T> = (inputLowerCase: string, option: T) => boolean;
type GetChipText<T> = (option: T) => string;

/**
 * Default chip component for selected items
 */
const DefaultChip: React.FC<{ text: string; onDelete?: () => void }> = ({ text, onDelete }) => (
  <DefaultChipContainer>
    <DefaultChipText>{text}</DefaultChipText>
    {onDelete && (
      <DefaultChipDeleteButton
        data-chip-delete="true"
        title="Remove"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
      >
        <Icon icon={['fad', 'times']} />
      </DefaultChipDeleteButton>
    )}
  </DefaultChipContainer>
);

type Props<T extends BaseOption> = {
  id?: string;
  options: T[];
  /** Controlled selected ids */
  selectedIds: string[];
  /** Emits updated selected list in id form and full option form */
  onChange: (selectedIds: string[], selectedOptions: T[]) => void;
  disabled?: boolean;
  width?: CSSProperties['width'];
  placeholder?: string;
  /** Placeholder for the search input inside the dropdown */
  searchPlaceholder?: string;
  /** If provided, limits visible suggestions and adds a "show more" row */
  suggestionsLimit?: number;
  /** Custom renderer for suggestion rows */
  renderOption?: RenderOption<T>;
  /** Custom chip text provider, defaults to option.name */
  getChipText?: GetChipText<T>;
  /** Custom selected chip renderer; receives option and onRemove handler */
  renderSelectedChip?: (option: T, onRemove?: () => void) => React.ReactNode;
  /** Custom filter, defaults to name includes */
  filterPredicate?: FilterPredicate<T>;
  /** Max height of the dropdown panel (px). Defaults to 360. */
  dropdownMaxHeight?: number;
  /** Show a create-new row when input doesn't match existing options. */
  allowCreateNew?: boolean;
  /** Called to create a new option; return the created option. */
  onCreateNew?: (input: string) => Promise<T | undefined>;
  /** Optional renderer for right-side action element that appears on row hover */
  renderOptionAction?: (option: T) => React.ReactNode;
  /** Optional footer content rendered at the bottom of the dropdown panel */
  dropdownFooter?: React.ReactNode;
  /** When true, forces the dropdown to close (e.g. when an external dialog opens over it) */
  isExternalDialogOpen?: boolean;
  hideClearAllButton?: boolean;
};

function filterOptions<T extends BaseOption>(
  inputLowerCase: string,
  options: T[],
  excludedIds: Set<string>,
  limit?: number,
  filterPredicate?: FilterPredicate<T>,
) {
  const filtered = options.filter((opt) => {
    if (excludedIds.has(opt.id)) {
      return false;
    }
    const matches = filterPredicate
      ? filterPredicate(inputLowerCase, opt)
      : opt.name.toLowerCase().includes(inputLowerCase);
    return matches;
  });

  return typeof limit === 'number' ? filtered.slice(0, limit) : filtered;
}

const MultiSelectDropdown = <T extends BaseOption>(props: Props<T>) => {
  const {
    id,
    options,
    selectedIds,
    onChange,
    disabled = false,
    placeholder = '',
    renderOption,
    getChipText,
    renderSelectedChip,
    filterPredicate,
    dropdownMaxHeight,
    allowCreateNew = false,
    onCreateNew,
    renderOptionAction,
    dropdownFooter,
    isExternalDialogOpen,
  } = props;

  const [inputValue, setInputValue] = React.useState('');
  const [isFocused, setIsFocused] = React.useState(false);
  const [visibleOptions, setVisibleOptions] = React.useState<T[]>([]);
  const [highlightedIndex, setHighlightedIndex] = React.useState<number>(-1);
  const [hoveredOptionId, setHoveredOptionId] = React.useState<string | null>(null);
  const excludedIdSet = React.useMemo(() => new Set(selectedIds), [selectedIds]);

  const searchInputRef = React.useRef<HTMLInputElement | null>(null);

  const clearAllConfirm = useDialog();
  const clearAllButtonRef = React.useRef<HTMLButtonElement>(null);

  const { t } = useTranslation();

  const { refs, strategy, update, floatingStyles } = useFloating({
    middleware: [offset(0), flip(), shift()],
    placement: 'bottom-start',
  });

  const { ref: componentRef, width: componentWidth } = useElementWidth();

  const allFiltered = React.useMemo(
    () =>
      filterOptions(inputValue.toLowerCase(), options, excludedIdSet, undefined, filterPredicate),
    [inputValue, options, excludedIdSet, filterPredicate],
  );

  React.useEffect(() => {
    setVisibleOptions(allFiltered);
  }, [allFiltered]);

  // Keep highlighted item stable across list updates when possible
  React.useEffect(() => {
    if (!isFocused) {
      setHighlightedIndex(-1);
      return;
    }
    if (visibleOptions.length === 0) {
      setHighlightedIndex(-1);
      return;
    }

    // If current highlighted option still exists, keep it; otherwise default to first
    const currentId = highlightedIndex >= 0 ? visibleOptions[highlightedIndex]?.id : undefined;
    if (currentId) {
      const newIndex = visibleOptions.findIndex((o) => o.id === currentId);
      if (newIndex !== -1) {
        setHighlightedIndex(newIndex);
        return;
      }
    }
    setHighlightedIndex(0);
  }, [isFocused, visibleOptions, highlightedIndex]);
  const inputTrimmed = inputValue.trim();
  const nameEquals = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();
  const existsSameName = options.some((o) => nameEquals(o.name, inputTrimmed));
  // Show create row whenever there's input and no exact match (partial matches allowed)
  const canCreate =
    isDefined(onCreateNew) && allowCreateNew && inputTrimmed.length > 0 && !existsSameName;
  const [creating, setCreating] = React.useState(false);

  const handleCreateNew = isDefined(onCreateNew)
    ? async () => {
        if (!onCreateNew || creating) {
          return;
        }
        try {
          setCreating(true);
          const created = await onCreateNew(inputTrimmed);
          if (created && created.id) {
            const nextIds = [...selectedIds, created.id];
            const nextOptions = [...options, created].filter((o) => nextIds.includes(o.id));
            onChange(nextIds, nextOptions);
            setInputValue('');
          }
        } finally {
          setCreating(false);
        }
      }
    : undefined;

  const handleEscapeKey = useKeyHandler(
    'Escape',
    () => {
      setIsFocused(false);
      setInputValue('');
    },
    { enabled: isFocused, stopPropagation: true },
  );

  // Key binding: Ctrl+Enter => create new
  const { handler: onCreateKeyHandler, hint: createKeyHint } = useKeyBinding({
    combo: 'Ctrl+Enter',
    onTrigger: () => {
      if (canCreate) {
        void handleCreateNew?.();
      }
    },
  });

  React.useEffect(() => {
    if (isFocused && refs.reference.current && refs.floating.current) {
      return autoUpdate(refs.reference.current, refs.floating.current, update, {
        ancestorResize: true,
        ancestorScroll: true,
        elementResize: true,
        animationFrame: true,
      });
    }
  }, [isFocused, refs.reference, refs.floating, update]);

  // Auto-focus search input when dropdown opens
  React.useEffect(() => {
    if (isFocused) {
      // Use setTimeout to ensure the FloatingPortal content is fully mounted
      const timeoutId = setTimeout(() => {
        searchInputRef.current?.focus();
        // Default highlight on focus
        if (visibleOptions.length > 0) {
          setHighlightedIndex(0);
        }
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [isFocused, visibleOptions.length]);

  // Ensure dropdown repositions when the field container height changes (chips wrap)
  const fieldRef = React.useRef<HTMLDivElement | null>(null);
  const setReference = React.useCallback(
    (node: HTMLDivElement | null) => {
      refs.setReference(node);
      fieldRef.current = node;
    },
    [refs],
  );

  React.useEffect(() => {
    if (!isFocused || !fieldRef.current) {
      return;
    }
    const ResizeObserverCtor: typeof ResizeObserver | undefined =
      typeof ResizeObserver !== 'undefined' ? ResizeObserver : undefined;
    if (!ResizeObserverCtor) {
      return;
    }
    const ro = new ResizeObserverCtor(() => {
      update();
    });
    ro.observe(fieldRef.current);
    return () => ro.disconnect();
  }, [isFocused, update]);

  React.useEffect(() => {
    if (isExternalDialogOpen) {
      setIsFocused(false);
    }
  }, [isExternalDialogOpen]);

  const onClickOutside = React.useCallback(
    (event: MouseEvent) => {
      const refEl = refs.reference.current;
      const floatEl = refs.floating.current;
      const target = event.target as Node;
      const targetEl = target as HTMLElement;
      const isControlClick = !!(
        targetEl &&
        (targetEl.closest('[data-chip-delete]') || targetEl.closest('[data-clear-all]'))
      );
      const insideRef = refEl instanceof HTMLElement && refEl.contains(target);
      const insideFloat = floatEl instanceof HTMLElement && floatEl.contains(target);
      if (insideRef || insideFloat) {
        if (!isFocused && !isControlClick) {
          setIsFocused(true);
        }
        return;
      }
      setIsFocused(false);
    },
    [isFocused, refs.reference, refs.floating],
  );

  React.useEffect(() => {
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [onClickOutside]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (!isFocused) {
      setIsFocused(true);
    }
  };

  const addOption = (opt: T) => {
    const nextIds = [...selectedIds, opt.id];
    const nextOptions = options.filter((o) => nextIds.includes(o.id));
    onChange(nextIds, nextOptions);
    setInputValue('');
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isFocused) {
      return;
    }

    // Allow key-binding hook to handle creation combo
    onCreateKeyHandler(e);
    if (e.defaultPrevented) {
      return;
    }

    const hasOptions = visibleOptions.length > 0;
    const maxIndex = visibleOptions.length - 1;
    switch (e.key) {
      case 'ArrowDown': {
        if (!hasOptions) {
          return;
        }
        e.preventDefault();
        setHighlightedIndex((idx) => (idx < 0 ? 0 : (idx + 1) % visibleOptions.length));
        break;
      }
      case 'ArrowUp': {
        if (!hasOptions) {
          return;
        }
        e.preventDefault();
        setHighlightedIndex((idx) => {
          if (idx < 0) {
            return maxIndex;
          }
          return idx - 1 < 0 ? maxIndex : idx - 1;
        });
        break;
      }
      case 'Enter': {
        // Enter selects highlighted suggestion (never the create row)
        if (highlightedIndex >= 0 && highlightedIndex < visibleOptions.length) {
          e.preventDefault();
          addOption(visibleOptions[highlightedIndex]);
        }
        break;
      }
      default:
        break;
    }
  };

  const removeOption = (id: string) => {
    const nextIds = selectedIds.filter((x) => x !== id);
    const nextOptions = options.filter((o) => nextIds.includes(o.id));
    onChange(nextIds, nextOptions);
  };

  const clearAll = () => {
    onChange([], []);
    setInputValue('');
  };

  const selectedOptions = React.useMemo(
    () => options.filter((x) => selectedIds.includes(x.id)),
    [options, selectedIds],
  );

  // No hidden concept; we always show all results with scroll

  return (
    <Component direction="column" ref={componentRef}>
      <FieldContainer
        $focused={isFocused}
        ref={setReference}
        onClick={(e) => {
          const target = e.target as HTMLElement;
          if (target.closest('[data-chip-delete]')) {
            return;
          }
          if (!disabled) {
            setIsFocused(true);
          }
        }}
      >
        <ChipsRow $hasChips={selectedOptions.length > 0} direction="row" spacing={0.5}>
          {selectedOptions.map((opt) => (
            <SelectedChipWrapper key={opt.id}>
              {renderSelectedChip ? (
                renderSelectedChip(opt, () => removeOption(opt.id))
              ) : (
                <DefaultChip
                  text={getChipText ? getChipText(opt) : opt.name}
                  onDelete={() => removeOption(opt.id)}
                />
              )}
            </SelectedChipWrapper>
          ))}

          {!props.hideClearAllButton && selectedOptions.length > 0 && (
            <ClearAllButton
              data-clear-all="true"
              data-testid="clear-all-btn"
              ref={clearAllButtonRef}
              title={t('multiSelectDropdown.clearAll', { ns: 'components' })}
              onClick={(e) => {
                e.stopPropagation();
                const rect = clearAllButtonRef.current?.getBoundingClientRect();
                clearAllConfirm.show(rect ? { x: rect.right, y: rect.top } : undefined);
              }}
            >
              <Icon icon={['fad', 'times']} />
            </ClearAllButton>
          )}

          {/* When unfocused and no tags, show placeholder text inside field */}
          {selectedOptions.length === 0 && <PlaceholderText>{placeholder}</PlaceholderText>}
        </ChipsRow>
      </FieldContainer>
      {isFocused && (
        <FloatingPortal>
          <DropdownContainer
            $maxHeight={dropdownMaxHeight ?? 360}
            ref={refs.setFloating}
            style={{ ...floatingStyles, position: strategy }}
            width={componentWidth}
          >
            <SearchBar>
              <TextBox
                disabled={disabled}
                id={`${id ?? 'multi-select'}-search`}
                placeholder={props.searchPlaceholder ?? ''}
                ref={searchInputRef}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={(e) => {
                  handleEscapeKey(e);
                  onKeyDown(e);
                }}
              />
            </SearchBar>

            <SuggestionsList className="notranslate" width={componentWidth}>
              {canCreate && (
                <Stack direction="row" spacing={0.5}>
                  <SuggestionItem onClick={() => void handleCreateNew?.()}>
                    {
                      <CreateRowContainer
                        alignItems="center"
                        direction="row"
                        justifyContent="space-between"
                        spacing={0.5}
                      >
                        <Stack alignItems="center" direction="row" spacing={0.5}>
                          <Icon icon={['fad', 'plus']} />
                          <DefaultChip text={inputTrimmed} />
                        </Stack>
                        {createKeyHint}
                      </CreateRowContainer>
                    }
                  </SuggestionItem>
                </Stack>
              )}
              {visibleOptions.map((opt, index) => (
                <SuggestionItem
                  $highlighted={index === highlightedIndex}
                  key={opt.id}
                  onClick={() => addOption(opt)}
                  onMouseEnter={() => {
                    setHoveredOptionId(opt.id);
                    setHighlightedIndex(index);
                  }}
                  onMouseLeave={() => setHoveredOptionId(null)}
                >
                  <SuggestionContent>
                    {renderOption ? renderOption(opt) : getChipText ? getChipText(opt) : opt.name}
                  </SuggestionContent>
                  {renderOptionAction && hoveredOptionId === opt.id && (
                    <SuggestionActionContainer
                      onClick={(e) => {
                        e?.stopPropagation();
                      }}
                    >
                      {renderOptionAction(opt)}
                    </SuggestionActionContainer>
                  )}
                </SuggestionItem>
              ))}
            </SuggestionsList>
            {isDefined(dropdownFooter) && dropdownFooter}
          </DropdownContainer>
        </FloatingPortal>
      )}

      {clearAllConfirm.render((pos) => (
        <ConfirmationDialog
          pos={pos}
          title={t('multiSelectDropdown.clearAll', { ns: 'components' })}
          onCancel={() => clearAllConfirm.hide()}
          onConfirm={async () => {
            await Promise.resolve(clearAll());
            clearAllConfirm.hide();
          }}
        >
          {t('multiSelectDropdown.clearAllConfirm', { ns: 'components' })}
        </ConfirmationDialog>
      ))}
    </Component>
  );
};

const Component = styled(Stack)`
  position: relative;
  width: 100%;
`;

const ChipsRow = styled(Stack)<{ $hasChips: boolean }>`
  align-items: center;
  flex-wrap: wrap;
  row-gap: 0.4em;
`;

const SelectedChipWrapper = styled.span`
  display: inline-flex;
`;

const FieldContainer = styled.div<{ $focused: boolean }>`
  display: block;
  width: 100%;
  padding: 0.4em;
  box-sizing: border-box;
  background: white;
  transition: background 0.2s ease-in-out;
  cursor: pointer;

  border: 1px solid white;

  ${(p) => p.$focused && `border: 1px solid ${p.theme.color.gray.light};`}

  &:hover {
    background: ${({ theme }) => theme.color.gray.lightest};
  }
`;

// const InputInline = styled.div`
//   display: inline-flex;
//   min-width: 140px;
//   flex: 1 1 140px;
// `;

const PlaceholderText = styled.span`
  color: ${({ theme }) => theme.color.gray.medium};
`;

// InputRow was used in the previous layout; no longer needed

const SuggestionsList = styled.ul<{ width: number }>`
  margin: 0;
  padding: 0;
  list-style: none;
  background: white;
  border: 1px solid #888;
  overflow-y: auto;
  z-index: 1000;
  width: ${({ width }) => width}px;
  box-sizing: border-box;
`;

const DropdownContainer = styled.div<{ width: number; $maxHeight: number }>`
  background: white;
  border: 1px solid #888;
  box-sizing: border-box;
  width: ${({ width }) => width}px;
  max-height: ${({ $maxHeight }) => $maxHeight}px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  z-index: 10001;
`;

const SearchBar = styled.div`
  padding: 0.4em;
  border-bottom: 1px solid #e0e0e0;
  background: #fafafa;
`;

const SuggestionItem = styled.li<{ $highlighted?: boolean }>`
  padding: 0.2em 0.5em;
  width: 100%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5em;

  &:hover {
    background: ${({ theme }) => theme.color.brand.light};
  }

  ${(p) => p.$highlighted && `background: ${p.theme.color.brand.light};`}
`;

const SuggestionContent = styled(Stack)`
  min-width: 0;
`;

const SuggestionActionContainer = styled(Stack)`
  opacity: 0.4;
  transition: opacity 0.2s ease-in-out;

  &:hover {
    opacity: 1;
  }
`;

const CreateRowContainer = styled(Stack)`
  width: 100%;
`;

// HiddenOptionsSpan removed; we always render full suggestion list

const ClearAllButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.1em 0.3em;
  font-size: 0.9em;
  line-height: 1;
  border: 1px solid #ccc;
  border-radius: 999px;
  background: #f8f8f8;
  color: #444;
  opacity: 0.8;
  cursor: pointer;

  &:hover {
    opacity: 1;
    background: #f0f0f0;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const DefaultChipContainer = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3em;
  padding: 0.25em 0.5em;
  background: ${({ theme }) => theme.color.brand.light};
  border: 1px solid ${({ theme }) => theme.color.brand.medium};
  border-radius: 4px;
  font-size: 0.9em;
  line-height: 1.2;
`;

const DefaultChipText = styled.span`
  color: ${({ theme }) => theme.color.gray.dark};
`;

const DefaultChipDeleteButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.color.gray.medium};
  cursor: pointer;
  font-size: 0.85em;
  line-height: 1;

  &:hover {
    color: ${({ theme }) => theme.color.gray.dark};
  }

  &:active {
    transform: scale(0.9);
  }
`;

// Activator removed: input now shows inline inside FieldContainer when focused or empty

export { MultiSelectDropdown };
